'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocale, useTranslations } from 'next-intl';
import { createClient } from '@/lib/supabase/browser';
import { Button } from '@/components/storefront/ui/button';
import { Field, Input } from '@/components/storefront/ui/input';
import { useState } from 'react';
import { EnvelopeSimple, SpinnerGap } from '@phosphor-icons/react';
import { Link } from '@/i18n/navigation';

const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordForm() {
  const t = useTranslations('auth');
  const locale = useLocale();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotPasswordValues) => {
    setServerError(null);
    setSuccessMessage(null);

    const supabase = createClient();
    const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery&locale=${locale}`,
    });

    if (error) {
      setServerError(error.message);
      return;
    }

    setSuccessMessage(t('checkEmailForReset'));
  };

  return (
    <div className="w-full max-w-md space-y-6">
      {successMessage && (
        <div className="bg-success/10 border border-success/20 text-success rounded-xl px-4 py-3 text-sm">
          {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <Field label={t('email')} error={errors.email?.message}>
          {(id, describedBy) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              size="lg"
              prefix={<EnvelopeSimple size={18} />}
              invalid={!!errors.email}
              aria-describedby={describedBy}
              placeholder="you@example.com"
              {...register('email')}
            />
          )}
        </Field>

        {serverError && (
          <p className="text-sm text-danger" role="alert">{serverError}</p>
        )}

        <Button
          type="submit"
          variant="solid"
          color="primary"
          size="lg"
          fullWidth
          disabled={isSubmitting}
          leftIcon={isSubmitting ? <SpinnerGap className="animate-spin" /> : null}
        >
          {t('sendResetLink')}
        </Button>
      </form>

      <p className="text-center text-sm text-muted-foreground">
        <Link href="/login" className="text-primary hover:underline font-medium">
          {t('backToLogin')}
        </Link>
      </p>
    </div>
  );
}
